import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { 
  Home, 
  Brain, 
  Moon, 
  Settings,
  Compass,
  Sparkles,
  Calendar,
  Utensils,
  MoreHorizontal,
  Printer,
  ShoppingCart,
  Heart
} from 'lucide-react';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Primary nav items (always visible)
  const primaryNavItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/brain-dump', icon: Brain, label: 'Journal' },
    { path: '/resources', icon: Sparkles, label: 'Ideas' },
    { path: '/review', icon: Moon, label: 'Review' },
  ];
  
  // More menu items
  const moreNavItems = [
    { path: '/planner', icon: Printer, label: 'Weekly Planner' },
    { path: '/meals', icon: Utensils, label: 'Meal Calendar' },
    { path: '/shopping', icon: ShoppingCart, label: 'Shopping List' },
    { path: '/saved-recipes', icon: Heart, label: 'Saved Ideas' },
    { path: '/settings', icon: Settings, label: 'Settings' },
  ];
  
  // All nav items for mobile
  const allNavItems = [
    { path: '/dashboard', icon: Home, label: 'Home' },
    { path: '/brain-dump', icon: Brain, label: 'Journal' },
    { path: '/meals', icon: Utensils, label: 'Meals' },
    { path: '/planner', icon: Calendar, label: 'Planner' },
    { path: '/review', icon: Moon, label: 'Review' },
  ];
  
  const isActive = (path) => location.pathname === path;
  const isMoreActive = moreNavItems.some(item => isActive(item.path));
  
  if (location.pathname === '/') {
    return <>{children}</>;
  }
  
  return (
    <div className="min-h-screen bg-paper paper-texture">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-paper/95 backdrop-blur-sm border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => navigate('/dashboard')}
              data-testid="header-logo"
            >
              <div className="w-10 h-10 rounded-full bg-brown flex items-center justify-center shadow-lifted">
                <Compass className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-lg text-ink-900">The Calm Keeper</h1>
                <p className="text-xs text-ink-500 font-accent italic">Compass</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {primaryNavItems.slice(1).map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                    onClick={() => navigate(item.path)}
                    variant="ghost"
                    size="icon"
                    className={`
                      transition-all
                      ${isActive(item.path) 
                        ? 'text-brown bg-brown/10' 
                        : 'text-ink-500 hover:text-sage hover:bg-sage-mist'
                      }
                    `}
                  >
                    <Icon className="w-5 h-5" />
                  </Button>
                );
              })}
              
              {/* More dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`
                      transition-all
                      ${isMoreActive 
                        ? 'text-brown bg-brown/10' 
                        : 'text-ink-500 hover:text-sage hover:bg-sage-mist'
                      }
                    `}
                  >
                    <MoreHorizontal className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {moreNavItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <DropdownMenuItem
                        key={item.path}
                        onClick={() => navigate(item.path)}
                        className={`cursor-pointer ${isActive(item.path) ? 'bg-brown/10 text-brown' : ''}`}
                      >
                        <Icon className="w-4 h-4 mr-2" />
                        {item.label}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Mobile header nav (simplified) */}
            <div className="flex md:hidden items-center gap-1">
              <Button
                onClick={() => navigate('/resources')}
                variant="ghost"
                size="icon"
                className={isActive('/resources') ? 'text-brown bg-brown/10' : 'text-ink-500'}
              >
                <Sparkles className="w-5 h-5" />
              </Button>
              <Button
                onClick={() => navigate('/settings')}
                variant="ghost"
                size="icon"
                className={isActive('/settings') ? 'text-brown bg-brown/10' : 'text-ink-500'}
              >
                <Settings className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main content */}
      <main className="max-w-2xl mx-auto px-4 py-8">
        {children}
      </main>
      
      {/* Mobile bottom navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-paper-light border-t border-stone-200 md:hidden z-50">
        <div className="flex items-center justify-around py-2">
          {allNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.path}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                onClick={() => navigate(item.path)}
                variant="ghost"
                className={`
                  flex-col gap-1 h-auto py-2 px-2
                  ${isActive(item.path) 
                    ? 'text-brown' 
                    : 'text-ink-500'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-accent">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </nav>
      
      <div className="h-20 md:hidden" />
    </div>
  );
};

export default Layout;
